/**
 * Frontend Validation for Insurance Calculation Form
 *
 * This module provides comprehensive client-side validation that matches
 * the backend FluentValidation rules to prevent unnecessary API calls
 * and provide immediate user feedback.
 */

export interface ValidationError {
  field: string;
  message: string;
  severity: "error" | "warning";
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// Valid insurer names (case-insensitive)
const VALID_MTPL_INSURERS = [
  "balcia",
  "balta",
  "ban",
  "bta",
  "compensa",
  "ergo",
  "gjensidige",
  "if",
];
const VALID_CASCO_INSURERS = [
  "balcia",
  "balta",
  "ban",
  "bta",
  "compensa",
  "ergo",
  "gjensidige",
  "if",
];

/**
 * Email validation regex
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Phone validation regex (digits, spaces, +, -, parentheses)
 */
const PHONE_REGEX = /^[\d\s+\-()]+$/;

/**
 * Trim whitespace from a string value
 */
const trimString = (value: any): string => {
  return typeof value === "string" ? value.trim() : "";
};

/**
 * Check if a value is empty (null, undefined, or empty string after trim)
 */
const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return trimString(value) === "";
  return false;
};

/**
 * Set date to midnight for comparison
 */
const setToMidnight = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * Validate product selection
 */
export const validateProductSelection = (
  selectedProducts: string[]
): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!selectedProducts || selectedProducts.length === 0) {
    errors.push({
      field: "selectedProducts",
      message: "qtg.calc.validation.productRequired",
      severity: "error",
    });
  } else {
    // Validate that products are lowercase "mtpl" or "casco"
    const invalidProducts = selectedProducts.filter(
      (p) => !["mtpl", "casco"].includes(p.toLowerCase())
    );
    if (invalidProducts.length > 0) {
      errors.push({
        field: "selectedProducts",
        message: "qtg.calc.validation.invalidProduct",
        severity: "error",
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate client information with conditional logic based on IsCompany
 */
export const validateClient = (
  client: any,
  fieldPrefix: string = "Client"
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Client object must exist
  if (!client) {
    errors.push({
      field: fieldPrefix,
      message: "qtg.calc.validation.clientRequired",
      severity: "error",
    });
    return { isValid: false, errors };
  }

  // Client Code is always required
  if (isEmpty(client.Code)) {
    errors.push({
      field: `${fieldPrefix}.Code`,
      message: "qtg.calc.validation.clientCodeRequired",
      severity: "error",
    });
  }

  // IsCompany must be explicitly set
  const isCompany = client.IsCompany === "true" || client.IsCompany === true;

  // Conditional validation based on IsCompany
  if (isCompany) {
    // Company: Name is required
    if (isEmpty(client.Name)) {
      errors.push({
        field: `${fieldPrefix}.Name`,
        message: "qtg.calc.validation.companyNameRequired",
        severity: "error",
      });
    }
  } else {
    // Individual: FirstName and LastName are required
    if (isEmpty(client.FirstName)) {
      errors.push({
        field: `${fieldPrefix}.FirstName`,
        message: "qtg.calc.validation.firstNameRequired",
        severity: "error",
      });
    }
    if (isEmpty(client.LastName)) {
      errors.push({
        field: `${fieldPrefix}.LastName`,
        message: "qtg.calc.validation.lastNameRequired",
        severity: "error",
      });
    }
  }

  // Address validation
  if (!client.Address) {
    errors.push({
      field: `${fieldPrefix}.Address`,
      message: "qtg.calc.validation.addressRequired",
      severity: "error",
    });
  } else {
    // AddressString is required
    if (isEmpty(client.Address.AddressString)) {
      errors.push({
        field: `${fieldPrefix}.Address.AddressString`,
        message: "qtg.calc.validation.addressRequired",
        severity: "error",
      });
    } else if (trimString(client.Address.AddressString).length < 5) {
      errors.push({
        field: `${fieldPrefix}.Address.AddressString`,
        message: "qtg.calc.validation.addressMinLength",
        severity: "warning",
      });
    }

    // At least one of Email OR Phone must be provided
    const hasEmail = !isEmpty(client.Address.Email);
    const hasPhone = !isEmpty(client.Address.Phone);

    if (!hasEmail && !hasPhone) {
      errors.push({
        field: `${fieldPrefix}.Address.Email`,
        message: "qtg.calc.validation.emailOrPhoneRequired",
        severity: "error",
      });
      errors.push({
        field: `${fieldPrefix}.Address.Phone`,
        message: "qtg.calc.validation.emailOrPhoneRequired",
        severity: "error",
      });
    } else {
      // Validate Email format if provided
      if (hasEmail && !EMAIL_REGEX.test(trimString(client.Address.Email))) {
        errors.push({
          field: `${fieldPrefix}.Address.Email`,
          message: "qtg.calc.validation.emailInvalid",
          severity: "error",
        });
      }

      // Validate Phone format if provided
      if (hasPhone && !PHONE_REGEX.test(trimString(client.Address.Phone))) {
        errors.push({
          field: `${fieldPrefix}.Address.Phone`,
          message: "qtg.calc.validation.phoneInvalid",
          severity: "error",
        });
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate vehicle owner information (simplified - no phone/email required)
 */
export const validateVehicleOwner = (
  owner: any,
  fieldPrefix: string = "Vehicle.Owner"
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Owner object must exist
  if (!owner) {
    errors.push({
      field: fieldPrefix,
      message: "qtg.calc.validation.ownerRequired",
      severity: "error",
    });
    return { isValid: false, errors };
  }

  // Owner Code is required
  if (isEmpty(owner.Code)) {
    errors.push({
      field: `${fieldPrefix}.Code`,
      message: "qtg.calc.validation.ownerCodeRequired",
      severity: "error",
    });
  }

  // Owner Name is required
  if (isEmpty(owner.Name)) {
    errors.push({
      field: `${fieldPrefix}.Name`,
      message: "qtg.calc.validation.ownerNameRequired",
      severity: "error",
    });
  }

  // Address validation (only AddressString, no phone/email for vehicle owner)
  if (owner.Address) {
    // AddressString validation if provided
    if (
      owner.Address.AddressString &&
      trimString(owner.Address.AddressString).length < 5
    ) {
      errors.push({
        field: `${fieldPrefix}.Address.AddressString`,
        message: "qtg.calc.validation.addressMinLength",
        severity: "warning",
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate vehicle information
 */
export const validateVehicle = (vehicle: any): ValidationResult => {
  const errors: ValidationError[] = [];

  // Vehicle object must exist
  if (!vehicle) {
    errors.push({
      field: "Vehicle",
      message: "qtg.calc.validation.vehicleRequired",
      severity: "error",
    });
    return { isValid: false, errors };
  }

  // RegistrationCertificateNumber is required for calculations
  if (isEmpty(vehicle.RegistrationCertificateNumber)) {
    errors.push({
      field: "Vehicle.RegistrationCertificateNumber",
      message: "qtg.calc.validation.registrationCertificateRequired",
      severity: "error",
    });
  }

  // LicensePlate is required
  if (isEmpty(vehicle.LicensePlate)) {
    errors.push({
      field: "Vehicle.LicensePlate",
      message: "qtg.calc.validation.licensePlateRequired",
      severity: "error",
    });
  } else {
    const licensePlate = trimString(vehicle.LicensePlate)
      .replace(/\s/g, "")
      .toUpperCase();
    if (licensePlate.length < 2) {
      errors.push({
        field: "Vehicle.LicensePlate",
        message: "qtg.calc.validation.licensePlateMinLength",
        severity: "warning",
      });
    }
  }

  // VIN is required (note: backend uses "Vin" not "VIN")
  if (isEmpty(vehicle.Vin)) {
    errors.push({
      field: "Vehicle.Vin",
      message: "qtg.calc.validation.vinRequired",
      severity: "error",
    });
  } else {
    const vin = trimString(vehicle.Vin).replace(/\s/g, "").toUpperCase();
    if (vin.length !== 17) {
      errors.push({
        field: "Vehicle.Vin",
        message: "qtg.calc.validation.vinLength",
        severity: "warning",
      });
    }
  }

  // Brand is required
  if (isEmpty(vehicle.Brand)) {
    errors.push({
      field: "Vehicle.Brand",
      message: "qtg.calc.validation.brandRequired",
      severity: "error",
    });
  }

  // Model is required
  if (isEmpty(vehicle.Model)) {
    errors.push({
      field: "Vehicle.Model",
      message: "qtg.calc.validation.modelRequired",
      severity: "error",
    });
  }

  // CubicCapacityCcm is required and must be positive integer
  if (isEmpty(vehicle.CubicCapacityCcm)) {
    errors.push({
      field: "Vehicle.CubicCapacityCcm",
      message: "qtg.calc.validation.engineCapacityRequired",
      severity: "error",
    });
  } else {
    const capacity = Number(vehicle.CubicCapacityCcm);
    if (isNaN(capacity) || capacity < 0 || !Number.isInteger(capacity)) {
      errors.push({
        field: "Vehicle.CubicCapacityCcm",
        message: "qtg.calc.validation.engineCapacityPositive",
        severity: "error",
      });
    }
  }

  // ManufactureDate or ManufactureYear is required
  // Accept either full date or just year
  if (isEmpty(vehicle.ManufactureDate) && isEmpty(vehicle.ManufactureYear)) {
    errors.push({
      field: "Vehicle.ManufactureDate",
      message: "qtg.calc.validation.manufactureDateRequired",
      severity: "error",
    });
  } else if (!isEmpty(vehicle.ManufactureDate)) {
    // If ManufactureDate is provided, validate it
    const manufactureDate = new Date(vehicle.ManufactureDate);
    const today = setToMidnight(new Date());
    const manufDate = setToMidnight(manufactureDate);

    if (isNaN(manufDate.getTime())) {
      errors.push({
        field: "Vehicle.ManufactureDate",
        message: "qtg.calc.validation.manufactureDateInvalid",
        severity: "error",
      });
    } else if (manufDate >= today) {
      errors.push({
        field: "Vehicle.ManufactureDate",
        message: "qtg.calc.validation.manufactureDatePast",
        severity: "error",
      });
    }
  } else if (!isEmpty(vehicle.ManufactureYear)) {
    // If only ManufactureYear is provided, validate it
    const year = Number(vehicle.ManufactureYear);
    const currentYear = new Date().getFullYear();

    if (isNaN(year) || year < 1900 || year > currentYear) {
      errors.push({
        field: "Vehicle.ManufactureDate",
        message: "qtg.calc.validation.manufactureYearRange",
        severity: "error",
      });
    }
  }

  // MaximumGrossWeight is required and must be positive
  if (isEmpty(vehicle.MaximumGrossWeight)) {
    errors.push({
      field: "Vehicle.MaximumGrossWeight",
      message: "qtg.calc.validation.weightRequired",
      severity: "error",
    });
  } else {
    const weight = Number(vehicle.MaximumGrossWeight);
    if (isNaN(weight) || weight < 0) {
      errors.push({
        field: "Vehicle.MaximumGrossWeight",
        message: "qtg.calc.validation.weightPositive",
        severity: "error",
      });
    }
  }

  // PowerKw is required and must be positive
  if (isEmpty(vehicle.PowerKw)) {
    errors.push({
      field: "Vehicle.PowerKw",
      message: "qtg.calc.validation.powerRequired",
      severity: "error",
    });
  } else {
    const power = Number(vehicle.PowerKw);
    if (isNaN(power) || power < 0) {
      errors.push({
        field: "Vehicle.PowerKw",
        message: "qtg.calc.validation.powerPositive",
        severity: "error",
      });
    }
  }

  // Vehicle Owner validation (simplified - no phone/email)
  if (!vehicle.Owner) {
    errors.push({
      field: "Vehicle.Owner",
      message: "qtg.calc.validation.ownerRequired",
      severity: "error",
    });
  } else {
    const ownerValidation = validateVehicleOwner(
      vehicle.Owner,
      "Vehicle.Owner"
    );
    errors.push(...ownerValidation.errors);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate insurance information
 */
export const validateInsurance = (
  insurance: any,
  selectedProducts?: string[],
  client?: any
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Insurance object must exist
  if (!insurance) {
    errors.push({
      field: "Insurance",
      message: "qtg.calc.validation.insuranceRequired",
      severity: "error",
    });
    return { isValid: false, errors };
  }

  // For CASCO, require at least one complete age/experience pair
  const isCasco = selectedProducts?.some(
    (product) => product.toLowerCase() === "casco"
  );

  if (isCasco) {
    // Coverage territory is mandatory for CASCO
    if (isEmpty(insurance.CoverageTerritory)) {
      errors.push({
        field: "Insurance.CoverageTerritory",
        message: "qtg.calc.validation.coverageTerritoryRequired",
        severity: "error",
      });
    }

    const hasClientAge = !isEmpty(client?.Age);
    const hasClientExperience = !isEmpty(client?.Experience); // Fixed: was DriverExperience, should be Experience
    const hasOtherDriverAge = !isEmpty(insurance.DriverAge);
    const hasOtherDriverExperience = !isEmpty(insurance.DriverExperience);

    const hasCompleteClientPair = hasClientAge && hasClientExperience;
    const hasCompleteOtherDriverPair =
      hasOtherDriverAge && hasOtherDriverExperience;

    if (!hasCompleteClientPair && !hasCompleteOtherDriverPair) {
      // Neither pair is complete
      if (
        !hasClientAge &&
        !hasClientExperience &&
        !hasOtherDriverAge &&
        !hasOtherDriverExperience
      ) {
        // Show error on both client age and other driver age fields
        errors.push({
          field: "Client.Age",
          message: "qtg.calc.validation.cascoAgeExperienceRequired",
          severity: "error",
        });
        errors.push({
          field: "Vehicle.OtherDriverAge",
          message: "qtg.calc.validation.cascoAgeExperienceRequired",
          severity: "error",
        });
      } else if (hasClientAge && !hasClientExperience) {
        errors.push({
          field: "Client.Experience",
          message: "qtg.calc.validation.clientExperienceRequired",
          severity: "error",
        });
      } else if (!hasClientAge && hasClientExperience) {
        errors.push({
          field: "Client.Age",
          message: "qtg.calc.validation.clientAgeRequired",
          severity: "error",
        });
      } else if (hasOtherDriverAge && !hasOtherDriverExperience) {
        errors.push({
          field: "Vehicle.OtherDriverExperience",
          message: "qtg.calc.validation.otherDriverExperienceRequired",
          severity: "error",
        });
      } else if (!hasOtherDriverAge && hasOtherDriverExperience) {
        errors.push({
          field: "Vehicle.OtherDriverAge",
          message: "qtg.calc.validation.otherDriverAgeRequired",
          severity: "error",
        });
      }
    }
  }

  // StartDate is required and must be today or in the future
  if (isEmpty(insurance.StartDate)) {
    errors.push({
      field: "Insurance.StartDate",
      message: "qtg.calc.validation.startDateRequired",
      severity: "error",
    });
  } else {
    const startDate = new Date(insurance.StartDate);
    const today = setToMidnight(new Date());
    const start = setToMidnight(startDate);

    if (isNaN(start.getTime())) {
      errors.push({
        field: "Insurance.StartDate",
        message: "qtg.calc.validation.startDateInvalid",
        severity: "error",
      });
    } else if (start < today) {
      errors.push({
        field: "Insurance.StartDate",
        message: "qtg.calc.validation.startDateFuture",
        severity: "error",
      });
    }
  }

  // DriverLicenceDate conditional validation
  if (
    !isEmpty(insurance.DriverBirthDate) &&
    !isEmpty(insurance.DriverLicenceDate)
  ) {
    const birthDate = new Date(insurance.DriverBirthDate);
    const licenceDate = new Date(insurance.DriverLicenceDate);
    const today = setToMidnight(new Date());
    const licence = setToMidnight(licenceDate);
    const birth = setToMidnight(birthDate);

    if (!isNaN(birth.getTime()) && !isNaN(licence.getTime())) {
      // Calculate minimum license date (15 years after birth)
      const minLicenseDate = new Date(birth);
      minLicenseDate.setFullYear(minLicenseDate.getFullYear() + 15);

      if (licence < minLicenseDate) {
        errors.push({
          field: "Insurance.DriverLicenceDate",
          message: "qtg.calc.validation.licenceDateMinAge",
          severity: "error",
        });
      }

      // License date must not be in the future
      if (licence > today) {
        errors.push({
          field: "Insurance.DriverLicenceDate",
          message: "qtg.calc.validation.licenceDateFuture",
          severity: "error",
        });
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate selected insurers (if provided)
 */
export const validateInsurers = (
  mtplInsurers?: string[],
  cascoInsurers?: string[]
): ValidationResult => {
  const errors: ValidationError[] = [];

  // MTPL insurers validation (optional, but if provided must be valid)
  if (mtplInsurers && mtplInsurers.length > 0) {
    const invalidMtpl = mtplInsurers.filter(
      (name) => !VALID_MTPL_INSURERS.includes(name.toLowerCase())
    );
    if (invalidMtpl.length > 0) {
      errors.push({
        field: "Mtpl.SelectedInsurers",
        message: "qtg.calc.validation.invalidMtplInsurers",
        severity: "error",
      });
    }
  }

  // CASCO insurers validation (optional, but if provided must be valid)
  if (cascoInsurers && cascoInsurers.length > 0) {
    const invalidCasco = cascoInsurers.filter(
      (name) => !VALID_CASCO_INSURERS.includes(name.toLowerCase())
    );
    if (invalidCasco.length > 0) {
      errors.push({
        field: "Casco.SelectedInsurers",
        message: "qtg.calc.validation.invalidCascoInsurers",
        severity: "error",
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate complete calculation request
 */
export const validateCalculationRequest = (request: any): ValidationResult => {
  const allErrors: ValidationError[] = [];

  // Validate product selection
  const productValidation = validateProductSelection(request.SelectedProducts);
  allErrors.push(...productValidation.errors);

  // Validate client
  const clientValidation = validateClient(request.Client);
  allErrors.push(...clientValidation.errors);

  // Validate vehicle
  const vehicleValidation = validateVehicle(request.Vehicle);
  allErrors.push(...vehicleValidation.errors);

  // Validate insurance
  const insuranceValidation = validateInsurance(
    request.Insurance,
    request.SelectedProducts,
    request.Client
  );
  allErrors.push(...insuranceValidation.errors);

  // Validate insurers (optional)
  const insurerValidation = validateInsurers(
    request.Mtpl?.SelectedInsurers,
    request.Casco?.SelectedInsurers
  );
  allErrors.push(...insurerValidation.errors);

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
  };
};

/**
 * Get field-specific errors from validation result
 */
export const getFieldErrors = (
  errors: ValidationError[],
  fieldPath: string
): ValidationError[] => {
  return errors.filter((error) => error.field === fieldPath);
};

/**
 * Check if field has errors
 */
export const hasFieldError = (
  errors: ValidationError[],
  fieldPath: string
): boolean => {
  return errors.some((error) => error.field === fieldPath);
};

/**
 * Get first error message for a field
 */
export const getFieldErrorMessage = (
  errors: ValidationError[],
  fieldPath: string
): string | undefined => {
  const fieldErrors = getFieldErrors(errors, fieldPath);
  return fieldErrors.length > 0 && fieldErrors[0]
    ? fieldErrors[0].message
    : undefined;
};
